import { useEffect, useRef } from 'react';
import { useFloorplan } from '@amzn/ihm-floorplan-viewer';

interface FloorplanSelectionBridgeProps {
  onSelectionChange?: (selectedFixtureIds: string[]) => void;
  selectionMode?: 'click' | 'rectangle';
  debounceMs?: number;
}

/**
 * Bridge component that connects FloorplanViewer's internal selection state
 * with external state management. Must be rendered as a child of FloorplanProvider.
 *
 * Includes debouncing to reduce update frequency during rapid selection changes.
 */
const FloorplanSelectionBridge = ({
  onSelectionChange,
  debounceMs = 100,
}: Omit<FloorplanSelectionBridgeProps, 'selectionMode'>) => {
  const { selectedFixtureIdsSet } = useFloorplan();
  const previousSelectionRef = useRef<Set<string>>(new Set());
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // Only notify if the selection actually changed
    const currentSelection = selectedFixtureIdsSet;
    const previousSelection = previousSelectionRef.current;

    // Compare sets to detect changes
    const hasChanged =
      currentSelection.size !== previousSelection.size ||
      Array.from(currentSelection).some((id) => !previousSelection.has(id));

    if (hasChanged && onSelectionChange) {
      // Clear any pending debounced call
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      // Debounce the selection change notification
      debounceTimerRef.current = setTimeout(() => {
        const selectedIds = Array.from(currentSelection);
        onSelectionChange(selectedIds);
        previousSelectionRef.current = new Set(currentSelection);
      }, debounceMs);
    }

    // Cleanup on unmount
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [selectedFixtureIdsSet, onSelectionChange, debounceMs]);

  // This component doesn't render anything
  return null;
};

export default FloorplanSelectionBridge;
